// src/services/firebase/auth.service.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  updateEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  onAuthStateChanged
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebaseconfig";

class AuthService {
  // Register new user
  async register(email, password, userData) {
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update display name if provided
      if (userData.displayName) {
        await updateProfile(user, {
          displayName: userData.displayName
        });
      }

      // Create user document in Firestore
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: userData.displayName || "",
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        phone: userData.phone || "",
        role: "customer", // Default role
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true
      });

      return {
        success: true,
        user: user,
        message: "Registration successful"
      };
    } catch (error) {
      console.error("Registration error:", error);
      return {
        success: false,
        error: error.code,
        message: this.getErrorMessage(error.code)
      };
    }
  }

  // Login user
  async login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Get user data from Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));
      
      if (!userDoc.exists()) {
        // Auto-create user document if it doesn't exist
        console.warn("User document not found, creating one...");
        await this.createMissingUserDocument(user);
        
        // Fetch the newly created document
        const newUserDoc = await getDoc(doc(db, "users", user.uid));
        const userData = newUserDoc.data();
        
        return {
          success: true,
          user: user,
          userData: userData,
          message: "Login successful"
        };
      }

      const userData = userDoc.data();

      // Check if user is active
      if (!userData.isActive) {
        await this.logout();
        throw new Error("Account is deactivated");
      }

      // Update last login
      await updateDoc(doc(db, "users", user.uid), {
        lastLogin: serverTimestamp()
      });

      return {
        success: true,
        user: user,
        userData: userData,
        message: "Login successful"
      };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error.code || error.message,
        message: this.getErrorMessage(error.code || error.message)
      };
    }
  }

  // Create missing user document for existing auth users
  async createMissingUserDocument(user) {
    try {
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || "",
        firstName: "",
        lastName: "",
        phone: "",
        role: "admin", // Default role
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        migratedAccount: true // Flag to identify auto-created documents
      });
      console.log("User document created successfully");
    } catch (error) {
      console.error("Error creating user document:", error);
      throw error;
    }
  }

  // Logout user
  async logout() {
    try {
      await signOut(auth);
      return {
        success: true,
        message: "Logout successful"
      };
    } catch (error) {
      console.error("Logout error:", error);
      return {
        success: false,
        error: error.code,
        message: this.getErrorMessage(error.code)
      };
    }
  }

  // Send password reset email
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return {
        success: true,
        message: "Password reset email sent"
      };
    } catch (error) {
      console.error("Password reset error:", error);
      return {
        success: false,
        error: error.code,
        message: this.getErrorMessage(error.code)
      };
    }
  }

  // Update user profile
  async updateUserProfile(userId, updates) {
    try {
      const user = auth.currentUser;

      // Update Firebase Auth profile if display name changed
      if (updates.displayName && user) {
        await updateProfile(user, {
          displayName: updates.displayName
        });
      }

      // Update Firestore document
      await updateDoc(doc(db, "users", userId), {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        message: "Profile updated successfully"
      };
    } catch (error) {
      console.error("Profile update error:", error);
      return {
        success: false,
        error: error.code,
        message: this.getErrorMessage(error.code)
      };
    }
  }

  // Update user email
  async updateUserEmail(newEmail, currentPassword) {
    try {
      const user = auth.currentUser;
      
      // Re-authenticate user before email change
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update email in Firebase Auth
      await updateEmail(user, newEmail);

      // Update email in Firestore
      await updateDoc(doc(db, "users", user.uid), {
        email: newEmail,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        message: "Email updated successfully"
      };
    } catch (error) {
      console.error("Email update error:", error);
      return {
        success: false,
        error: error.code,
        message: this.getErrorMessage(error.code)
      };
    }
  }

  // Update user password
  async updateUserPassword(currentPassword, newPassword) {
    try {
      const user = auth.currentUser;
      
      // Re-authenticate user before password change
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      return {
        success: true,
        message: "Password updated successfully"
      };
    } catch (error) {
      console.error("Password update error:", error);
      return {
        success: false,
        error: error.code,
        message: this.getErrorMessage(error.code)
      };
    }
  }

  // Get current user data from Firestore
  async getCurrentUserData() {
    try {
      const user = auth.currentUser;
      if (!user) {
        return null;
      }

      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        // Auto-create if missing
        await this.createMissingUserDocument(user);
        const newUserDoc = await getDoc(doc(db, "users", user.uid));
        return {
          ...newUserDoc.data(),
          uid: user.uid
        };
      }

      return {
        ...userDoc.data(),
        uid: user.uid
      };
    } catch (error) {
      console.error("Get user data error:", error);
      return null;
    }
  }

  // Check if user is admin
  async isAdmin(userId) {
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (!userDoc.exists()) {
        return false;
      }

      const userData = userDoc.data();
      return userData.role === "admin" || userData.role === "super-admin";
    } catch (error) {
      console.error("Check admin error:", error);
      return false;
    }
  }

  // Auth state observer
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
  }

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  }

  // Get user-friendly error messages
  getErrorMessage(errorCode) {
    const errorMessages = {
      "auth/email-already-in-use": "This email is already registered",
      "auth/invalid-email": "Invalid email address",
      "auth/operation-not-allowed": "Operation not allowed",
      "auth/weak-password": "Password is too weak. Use at least 6 characters",
      "auth/user-disabled": "This account has been disabled",
      "auth/user-not-found": "No account found with this email",
      "auth/wrong-password": "Incorrect password",
      "auth/too-many-requests": "Too many attempts. Please try again later",
      "auth/network-request-failed": "Network error. Please check your connection",
      "auth/invalid-credential": "Invalid credentials provided",
      "auth/requires-recent-login": "Please log in again to complete this action",
      "Account is deactivated": "Your account has been deactivated. Contact support.",
      "User data not found": "User data not found. Please contact support."
    };

    return errorMessages[errorCode] || "An error occurred. Please try again.";
  }
}

export default new AuthService();